/* tk-carousel.js — shared controllers for the Threadkins rail sections
   (tk-occasion-carousel, tk-tabbed-products).

   <tk-carousel> — a free-scrolling rail (native overflow-x) with:
     • synced progress scrollbar (draggable thumb, click-to-jump track)
     • hover-reveal prev/next arrows that page by ~90% of the viewport
     • mouse drag-to-scroll (touch stays native); the click that ends a real
       drag is suppressed so card links don't fire
     • keyboard support on the track (arrows/Home/End) + reduced-motion aware
   Expected children (all optional except the track):
     [data-tk-track], [data-tk-bar] > [data-tk-thumb], [data-tk-prev], [data-tk-next]

   <tk-tabs> — accessible tab strip whose tabs are REAL collection links
   (role=tab <a>s): JS intercepts activation and toggles pre-rendered panels,
   so no-JS/SEO falls through to the collection page — same progressive
   enhancement Urbanstems uses. Roving tabindex + arrow keys per APG.
   Expected: [data-tk-tab] anchors, [data-tk-panel] panels (same order),
   optional [data-tk-shopall][data-auto] link that follows the active tab.

   Vanilla, no dependencies. Safe to load from multiple sections (define guard),
   safe across theme-editor re-renders (AbortController re-bind). */
(() => {
  'use strict';
  if (customElements.get('tk-carousel')) return;

  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

  class TkCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-tk-track]');
      if (!this.track) return;
      this.bar = this.querySelector('[data-tk-bar]');
      this.thumb = this.querySelector('[data-tk-thumb]');
      this.prev = this.querySelector('[data-tk-prev]');
      this.next = this.querySelector('[data-tk-next]');
      if (this.ac) this.ac.abort();
      this.ac = new AbortController();
      const o = { signal: this.ac.signal };

      this.track.addEventListener('scroll', () => this.queue(), o);
      addEventListener('resize', () => this.queue(), o);
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.queue());
        this.ro.observe(this.track);
      }
      this.prev && this.prev.addEventListener('click', () => this.page(-1), o);
      this.next && this.next.addEventListener('click', () => this.page(1), o);
      this.track.addEventListener('keydown', (e) => this.onKey(e), o);
      this.track.querySelectorAll('img').forEach((img) => {
        if (!img.complete) img.addEventListener('load', () => this.queue(), o);
      });
      this.bindThumb(o);
      this.bindDrag(o);
      this.refresh();
    }
    disconnectedCallback() {
      if (this.ac) this.ac.abort();
      if (this.ro) this.ro.disconnect();
      this.ro = null;
    }
    get max() { return this.track.scrollWidth - this.track.clientWidth; }
    behavior() { return reduced() ? 'auto' : 'smooth'; }
    queue() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => { this.raf = 0; this.refresh(); });
    }
    refresh() {
      if (!this.track) return;
      const max = this.max;
      this.classList.toggle('is-static', max < 5);
      if (this.bar && this.thumb) {
        const tw = this.bar.clientWidth;
        const w = Math.max((tw * this.track.clientWidth) / Math.max(this.track.scrollWidth, 1), 40);
        const x = max > 0 ? (this.track.scrollLeft / max) * (tw - w) : 0;
        this.thumb.style.width = w + 'px';
        this.thumb.style.transform = 'translateX(' + x + 'px)';
      }
      if (this.prev) this.prev.toggleAttribute('disabled', this.track.scrollLeft < 4);
      if (this.next) this.next.toggleAttribute('disabled', this.track.scrollLeft > max - 4);
    }
    page(dir) {
      this.track.scrollBy({ left: dir * this.track.clientWidth * 0.9, behavior: this.behavior() });
    }
    step() {
      const c = this.track.firstElementChild;
      if (!c) return 300;
      const s = getComputedStyle(this.track);
      return c.getBoundingClientRect().width + (parseFloat(s.columnGap) || 0);
    }
    onKey(e) {
      if (e.target !== this.track) return;
      if (e.key === 'ArrowRight') { this.track.scrollBy({ left: this.step(), behavior: this.behavior() }); }
      else if (e.key === 'ArrowLeft') { this.track.scrollBy({ left: -this.step(), behavior: this.behavior() }); }
      else if (e.key === 'Home') { this.track.scrollTo({ left: 0, behavior: this.behavior() }); }
      else if (e.key === 'End') { this.track.scrollTo({ left: this.max, behavior: this.behavior() }); }
      else return;
      e.preventDefault();
    }
    bindThumb(o) {
      if (!this.bar || !this.thumb) return;
      let sx = 0, sl = 0, on = false;
      this.thumb.addEventListener('pointerdown', (e) => {
        on = true; sx = e.clientX; sl = this.track.scrollLeft;
        this.thumb.setPointerCapture(e.pointerId);
        this.classList.add('is-scrubbing');
        e.preventDefault();
      }, o);
      this.thumb.addEventListener('pointermove', (e) => {
        if (!on) return;
        const tw = this.bar.clientWidth;
        const w = this.thumb.getBoundingClientRect().width;
        this.track.scrollLeft = sl + (e.clientX - sx) * (this.max / Math.max(tw - w, 1));
      }, o);
      const end = () => { on = false; this.classList.remove('is-scrubbing'); };
      this.thumb.addEventListener('pointerup', end, o);
      this.thumb.addEventListener('pointercancel', end, o);
      this.bar.addEventListener('pointerdown', (e) => {
        if (e.target === this.thumb) return;
        const r = this.bar.getBoundingClientRect();
        const w = this.thumb.getBoundingClientRect().width;
        const frac = Math.min(Math.max((e.clientX - r.left - w / 2) / Math.max(r.width - w, 1), 0), 1);
        this.track.scrollTo({ left: frac * this.max, behavior: this.behavior() });
      }, o);
    }
    bindDrag(o) {
      let down = false, dragged = false, sx = 0, sl = 0;
      this.track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        down = true; dragged = false; sx = e.clientX; sl = this.track.scrollLeft;
      }, o);
      this.track.addEventListener('pointermove', (e) => {
        if (!down) return;
        // recover from a release that happened outside the track (before 6px
        // capture, the window enders below never armed): if no button is held,
        // the drag is stale — clear it and stop rather than jumping the scroll.
        if (e.buttons === 0) { up(); return; }
        const dx = e.clientX - sx;
        if (!dragged && Math.abs(dx) > 6) {
          dragged = true;
          this.track.setPointerCapture(e.pointerId);
          this.classList.add('is-dragging');
        }
        if (dragged) this.track.scrollLeft = sl - dx;
      }, o);
      const up = () => {
        down = false;
        // keep the flag through the click that follows pointerup, then clear
        requestAnimationFrame(() => { this.classList.remove('is-dragging'); dragged = false; });
      };
      // bind enders to WINDOW (signal-scoped, removed on disconnect) so a release
      // outside the track still clears drag state at release time, not lazily.
      addEventListener('pointerup', up, o);
      addEventListener('pointercancel', up, o);
      this.track.addEventListener('click', (e) => {
        if (dragged) { e.preventDefault(); e.stopPropagation(); }
      }, { capture: true, signal: o.signal });
    }
  }
  customElements.define('tk-carousel', TkCarousel);

  class TkTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[data-tk-tab]')];
      this.panels = [...this.querySelectorAll('[data-tk-panel]')];
      if (!this.tabs.length) return;
      if (this.ac) this.ac.abort();
      this.ac = new AbortController();
      const o = { signal: this.ac.signal };
      this.tabs.forEach((t, i) => {
        t.addEventListener('click', (e) => {
          // Preserve the anchor's native new-tab/new-window behavior. Only a
          // plain primary activation is enhanced into an in-page tab switch.
          if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          this.activate(i);
        }, o);
        t.addEventListener('keydown', (e) => this.onKey(e, i), o);
      });
      const start = this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
      this.activate(start < 0 ? 0 : start);
    }
    disconnectedCallback() { if (this.ac) this.ac.abort(); }
    onKey(e, i) {
      let n = null;
      if (e.key === 'ArrowRight') n = (i + 1) % this.tabs.length;
      else if (e.key === 'ArrowLeft') n = (i - 1 + this.tabs.length) % this.tabs.length;
      else if (e.key === 'Home') n = 0;
      else if (e.key === 'End') n = this.tabs.length - 1;
      else if (e.key === ' ' || e.key === 'Spacebar') n = i;
      if (n === null) return;
      e.preventDefault();
      this.activate(n);
      this.tabs[n].focus();
    }
    activate(i) {
      this.tabs.forEach((t, j) => {
        const on = i === j;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.classList.toggle('is-active', on);
        t.tabIndex = on ? 0 : -1;
      });
      this.panels.forEach((p, j) => { p.hidden = j !== i; });
      const all = this.querySelector('[data-tk-shopall][data-auto]');
      if (all && this.tabs[i].getAttribute('href')) all.href = this.tabs[i].getAttribute('href');
      const car = this.panels[i] && this.panels[i].querySelector('tk-carousel');
      if (car && car.refresh) car.refresh();
    }
  }
  customElements.define('tk-tabs', TkTabs);

  // Theme editor: selecting a card block scrolls it into view; selecting a tab
  // block activates its tab so the merchant sees what they're editing.
  document.addEventListener('shopify:block:select', (e) => {
    const el = e.target;
    if (!el || !el.closest) return;
    const tabs = el.closest('tk-tabs');
    if (tabs && tabs.tabs) {
      const idx = tabs.tabs.findIndex((t) => t === el || el.contains(t) || t.contains(el));
      if (idx > -1) { tabs.activate(idx); return; }
      const p = el.closest('[data-tk-panel]');
      if (p) tabs.activate(tabs.panels.indexOf(p));
    }
    if (el.closest('tk-carousel')) el.scrollIntoView({ behavior: 'auto', inline: 'center', block: 'nearest' });
  });
})();
